import { Badge, Checkbox, ColorSwatch, Group, NumberInput, Slider, Stack, Switch, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import {
  CATEGORY_COLORS,
  CATEGORY_KEYS,
  MAX_EDIT_DISTANCE_LIMIT,
  MAX_SUPPRESSION_RADIUS_M,
  type CategoryKey,
  type MatchingParams,
} from './comparison'

export function MatchingParamsControls({
  params,
  onChange,
}: {
  params: MatchingParams
  onChange: (params: MatchingParams) => void
}) {
  const { t } = useTranslation('detectionComparison')

  return (
    <Stack gap="sm">
      <Text fw={600}>{t('matching.section')}</Text>
      <NumberInput
        label={t('matching.maxDistance')}
        min={0}
        step={0.5}
        value={params.maxDistanceM}
        onChange={value => {
          if (typeof value === 'number') onChange({ ...params, maxDistanceM: value })
        }}
      />
      <div>
        <Text size="sm">{t('matching.maxEditDistance')}</Text>
        <Slider
          min={0}
          max={MAX_EDIT_DISTANCE_LIMIT}
          step={1}
          marks={Array.from({ length: MAX_EDIT_DISTANCE_LIMIT + 1 }, (_, value) => ({ value, label: String(value) }))}
          defaultValue={params.maxEditDistance}
          onChangeEnd={value => onChange({ ...params, maxEditDistance: value })}
          mb="md"
        />
      </div>
      <div>
        <Text size="sm">{t('matching.suppressionRadius')}</Text>
        <Slider
          min={0}
          max={MAX_SUPPRESSION_RADIUS_M}
          step={0.5}
          marks={[0, MAX_SUPPRESSION_RADIUS_M / 2, MAX_SUPPRESSION_RADIUS_M].map(value => ({
            value,
            label: String(value),
          }))}
          defaultValue={params.suppressionRadiusM}
          onChangeEnd={value => onChange({ ...params, suppressionRadiusM: value })}
          mb="md"
        />
      </div>
    </Stack>
  )
}

export const useCategoryLabel = (firstDriveId: number, secondDriveId: number) => {
  const { t } = useTranslation('detectionComparison')
  return (key: CategoryKey): string => {
    switch (key) {
      case 'matched':
        return t('categories.matched')
      case 'too_far':
        return t('categories.too_far')
      case 'seen_once_first':
        return t('categories.seen_once', { id: firstDriveId })
      case 'seen_once_second':
        return t('categories.seen_once', { id: secondDriveId })
    }
  }
}

const FilterRow = ({
  label,
  color,
  count,
  checked,
  onChange,
}: {
  label: string
  color?: string
  count: number
  checked: boolean
  onChange: (checked: boolean) => void
}) => (
  <Checkbox
    checked={checked}
    onChange={event => onChange(event.currentTarget.checked)}
    label={
      <Group gap="xs" wrap="nowrap">
        {color && <ColorSwatch color={color} size={12} />}
        <Text size="sm">{label}</Text>
        <Badge size="sm" variant="light" color="gray">
          {count}
        </Badge>
      </Group>
    }
  />
)

export function DisplayFilters({
  firstDriveId,
  secondDriveId,
  categoryCounts,
  driveCounts,
  hiddenCategories,
  hiddenDrives,
  showPairLines,
  showRejectedLinks,
  onToggleCategory,
  onToggleDrive,
  onShowPairLinesChange,
  onShowRejectedLinksChange,
}: {
  firstDriveId: number
  secondDriveId: number
  categoryCounts: Record<CategoryKey, number>
  driveCounts: Record<number, number>
  hiddenCategories: ReadonlySet<CategoryKey>
  hiddenDrives: ReadonlySet<number>
  showPairLines: boolean
  showRejectedLinks: boolean
  onToggleCategory: (key: CategoryKey, visible: boolean) => void
  onToggleDrive: (driveId: number, visible: boolean) => void
  onShowPairLinesChange: (value: boolean) => void
  onShowRejectedLinksChange: (value: boolean) => void
}) {
  const { t } = useTranslation('detectionComparison')
  const categoryLabel = useCategoryLabel(firstDriveId, secondDriveId)

  return (
    <Stack gap="sm">
      <Text fw={600}>{t('display.section')}</Text>
      <Text size="sm" c="dimmed">
        {t('display.categories')}
      </Text>
      {CATEGORY_KEYS.map(key => (
        <FilterRow
          key={key}
          label={categoryLabel(key)}
          color={CATEGORY_COLORS[key]}
          count={categoryCounts[key]}
          checked={!hiddenCategories.has(key)}
          onChange={visible => onToggleCategory(key, visible)}
        />
      ))}
      <Text size="sm" c="dimmed">
        {t('display.drives')}
      </Text>
      {[firstDriveId, secondDriveId].map(driveId => (
        <FilterRow
          key={driveId}
          label={t('display.drive', { id: driveId })}
          count={driveCounts[driveId] ?? 0}
          checked={!hiddenDrives.has(driveId)}
          onChange={visible => onToggleDrive(driveId, visible)}
        />
      ))}
      <Switch
        label={t('display.pairLines')}
        checked={showPairLines}
        onChange={event => onShowPairLinesChange(event.currentTarget.checked)}
      />
      <Switch
        label={t('display.rejectedLinks')}
        checked={showRejectedLinks}
        onChange={event => onShowRejectedLinksChange(event.currentTarget.checked)}
      />
    </Stack>
  )
}
